/**
 * ==========================================================================
 * BARBERHOUSE — HERO DOUBLE PANNEAU
 * ==========================================================================
 *
 * 08/10/2026 — v2 :
 * - Glissement horizontal entre les images (au lieu du fondu).
 * - Glisser au doigt sur mobile (et à la souris sur ordinateur) :
 *   l'image suit le doigt ; un geste vertical fait défiler la page.
 * - Flèches gauche / droite centrées sur l'image (remplacent les points).
 * - Défilement automatique fiabilisé :
 *     · pause au survol uniquement avec une vraie souris
 *       (avant : un simple toucher bloquait le défilement) ;
 *     · pause au focus uniquement en navigation clavier ;
 *     · reprise automatique 4 s après une interaction tactile ;
 *     · arrêt quand l'onglet est masqué ;
 *     · respect du réglage système « Réduire les animations ».
 * - Préchargement des images suivantes (évite les blancs).
 * - Chaque .bh-panel est indépendant. Aucun framework.
 * ==========================================================================
 */

(function () {
  'use strict';

  var ANIM_MS = 450;
  var RESUME_AFTER_TOUCH_MS = 4000;
  var DRAG_START_PX = 8;
  var SWIPE_RATIO = 0.18; /* part de la largeur à parcourir pour valider */
  var SWIPE_FAST_PX_MS = 0.45; /* geste rapide : valide même court */

  function initPanel(panel) {
    if (!panel || panel.dataset.sliderInitialized === 'true') return;
    panel.dataset.sliderInitialized = 'true';

    var frames = Array.prototype.slice.call(panel.querySelectorAll('.bh-frame'));
    var n = frames.length;
    if (n === 0) return;

    var prevBtn = panel.querySelector('[data-bh-prev]');
    var nextBtn = panel.querySelector('[data-bh-next]');

    var speed = parseInt(panel.getAttribute('data-autoplay'), 10);
    if (isNaN(speed) || speed < 1) speed = 6;
    var autoplayDelay = speed * 1000;

    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    var current = 0;
    var timer = null;
    var cleanupTimer = null;
    var pendingCleanup = null;

    var isHovering = false; /* souris uniquement */
    var isKeyboardFocus = false;
    var isTouchPaused = false;
    var touchResumeTimer = null;

    /* ------------------------------------------------------------------
       Positionnement des diapositives
       ------------------------------------------------------------------ */

    function setPos(frame, percent, px, animate) {
      frame.style.transition = animate ? '' : 'none';
      frame.style.transform = 'translateX(calc(' + percent + '% + ' + (px || 0) + 'px))';
    }

    function setAria() {
      frames.forEach(function (frame, i) {
        var active = i === current;
        frame.classList.toggle('active', active);
        frame.setAttribute('aria-hidden', active ? 'false' : 'true');
        /* liens et boutons des diapositives masquées hors tabulation */
        frame.querySelectorAll('a, button').forEach(function (el) {
          if (active) {
            el.removeAttribute('tabindex');
          } else {
            el.setAttribute('tabindex', '-1');
          }
        });
      });
    }

    function runPendingCleanup() {
      if (cleanupTimer) {
        window.clearTimeout(cleanupTimer);
        cleanupTimer = null;
      }
      if (pendingCleanup) {
        var fn = pendingCleanup;
        pendingCleanup = null;
        fn();
      }
    }

    function resetFrame(frame) {
      frame.style.transition = 'none';
      frame.style.transform = '';
      frame.style.visibility = '';
    }

    /**
     * Passe à la diapositive index.
     * dir : 1 = vient de la droite (suivante), -1 = vient de la gauche.
     * fromDrag : position actuelle déjà décalée par le doigt.
     */
    function goTo(index, dir, fromDrag) {
      index = ((index % n) + n) % n;
      if (index === current) return;
      runPendingCleanup();

      var from = frames[current];
      var to = frames[index];

      if (!fromDrag) {
        to.style.visibility = 'visible';
        setPos(to, dir * 100, 0, false);
        void to.offsetWidth; /* force le rendu avant l'animation */
      }

      setPos(from, -dir * 100, 0, true);
      setPos(to, 0, 0, true);

      current = index;
      setAria();
      preloadAround();

      pendingCleanup = function () {
        resetFrame(from);
        resetFrame(to);
      };
      cleanupTimer = window.setTimeout(runPendingCleanup, reducedMotion.matches ? 0 : ANIM_MS + 30);
    }

    function next() { goTo(current + 1, 1); }
    function prev() { goTo(current - 1, -1); }

    /* ------------------------------------------------------------------
       Défilement automatique
       ------------------------------------------------------------------ */

    function canAutoplay() {
      return n > 1 && !reducedMotion.matches && !document.hidden &&
        !isHovering && !isKeyboardFocus && !isTouchPaused && !drag.active;
    }

    function stop() {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
    }

    function start() {
      stop();
      if (!canAutoplay()) return;
      timer = window.setTimeout(function () {
        timer = null;
        if (canAutoplay()) next();
        start();
      }, autoplayDelay);
    }

    function pauseForTouch() {
      isTouchPaused = true;
      stop();
      if (touchResumeTimer) window.clearTimeout(touchResumeTimer);
    }

    function resumeAfterTouch() {
      if (touchResumeTimer) window.clearTimeout(touchResumeTimer);
      touchResumeTimer = window.setTimeout(function () {
        isTouchPaused = false;
        start();
      }, RESUME_AFTER_TOUCH_MS);
    }

    /* ------------------------------------------------------------------
       Préchargement des images (version affichée seulement)
       ------------------------------------------------------------------ */

    function preloadFrame(frame) {
      if (!frame || frame.dataset.bhPreloaded === 'true') return;
      frame.dataset.bhPreloaded = 'true';
      frame.querySelectorAll('img').forEach(function (img) {
        if (window.getComputedStyle(img).display !== 'none' && img.loading === 'lazy') {
          img.loading = 'eager';
        }
      });
    }

    function preloadAround() {
      preloadFrame(frames[(current + 1) % n]);
      preloadFrame(frames[(current - 1 + n) % n]);
    }

    /* ------------------------------------------------------------------
       Flèches
       ------------------------------------------------------------------ */

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        prev();
        start();
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        next();
        start();
      });
    }

    /* ------------------------------------------------------------------
       Glisser (doigt / souris / stylet)
       ------------------------------------------------------------------ */

    var drag = {
      active: false, /* glissement horizontal confirmé */
      tracking: false,
      id: null,
      type: '',
      x0: 0,
      y0: 0,
      t0: 0,
      dx: 0,
      side: 0,
      neighbor: null,
      suppressClick: false
    };

    function isDragZone(target) {
      return target && target.closest &&
        target.closest('.bh-frame__image-link, .bh-frame > .bh-frame__visual') &&
        !target.closest('.bh-arrow');
    }

    function placeNeighbor(dx) {
      var side = dx < 0 ? 1 : -1;
      if (side !== drag.side) {
        if (drag.neighbor) resetFrame(drag.neighbor);
        drag.side = side;
        drag.neighbor = frames[(current + side + n) % n];
        drag.neighbor.style.visibility = 'visible';
        preloadFrame(drag.neighbor);
      }
      setPos(frames[current], 0, dx, false);
      if (drag.neighbor !== frames[current]) {
        setPos(drag.neighbor, side * 100, dx, false);
      }
    }

    panel.addEventListener('pointerdown', function (e) {
      if (n < 2 || drag.tracking) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (!isDragZone(e.target)) return;

      runPendingCleanup();
      drag.tracking = true;
      drag.active = false;
      drag.id = e.pointerId;
      drag.type = e.pointerType;
      drag.x0 = e.clientX;
      drag.y0 = e.clientY;
      drag.t0 = Date.now();
      drag.dx = 0;
      drag.side = 0;
      drag.neighbor = null;
      drag.suppressClick = false;

      if (e.pointerType === 'mouse') {
        e.preventDefault(); /* évite le glisser-déposer natif du lien / de l'image */
      } else {
        pauseForTouch();
      }
    });

    panel.addEventListener('pointermove', function (e) {
      if (!drag.tracking || e.pointerId !== drag.id) return;
      var dx = e.clientX - drag.x0;
      var dy = e.clientY - drag.y0;

      if (!drag.active) {
        if (Math.abs(dx) < DRAG_START_PX && Math.abs(dy) < DRAG_START_PX) return;
        if (Math.abs(dy) > Math.abs(dx)) {
          /* geste vertical : on laisse la page défiler */
          drag.tracking = false;
          if (drag.type !== 'mouse') resumeAfterTouch();
          return;
        }
        drag.active = true;
        stop();
        try { panel.setPointerCapture(e.pointerId); } catch (err) {}
      }

      drag.dx = dx;
      placeNeighbor(dx);
    });

    function endDrag(e, cancelled) {
      if (!drag.tracking || (e && e.pointerId !== drag.id)) return;
      drag.tracking = false;

      if (drag.active) {
        drag.active = false;
        drag.suppressClick = true;
        window.setTimeout(function () { drag.suppressClick = false; }, 350);

        var width = panel.offsetWidth || 1;
        var elapsed = Math.max(1, Date.now() - drag.t0);
        var fast = Math.abs(drag.dx) / elapsed > SWIPE_FAST_PX_MS && Math.abs(drag.dx) > 30;
        var commit = !cancelled && (Math.abs(drag.dx) > width * SWIPE_RATIO || fast);

        if (commit && drag.neighbor) {
          goTo(current + drag.side, drag.side, true);
        } else {
          /* retour en place */
          var back = drag.neighbor;
          var side = drag.side;
          setPos(frames[current], 0, 0, true);
          if (back && back !== frames[current]) setPos(back, side * 100, 0, true);
          runPendingCleanup();
          pendingCleanup = function () {
            if (back && back !== frames[current]) resetFrame(back);
            resetFrame(frames[current]);
          };
          cleanupTimer = window.setTimeout(runPendingCleanup, ANIM_MS + 30);
        }
        drag.neighbor = null;
        drag.side = 0;
      }

      if (drag.type === 'mouse') {
        start();
      } else {
        resumeAfterTouch();
      }
    }

    panel.addEventListener('pointerup', function (e) { endDrag(e, false); });
    panel.addEventListener('pointercancel', function (e) { endDrag(e, true); });

    /* Un glissement ne doit pas ouvrir le lien de l'image */
    panel.addEventListener('click', function (e) {
      if (drag.suppressClick) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);

    /* Empêche le glisser-déposer natif des liens et images */
    panel.addEventListener('dragstart', function (e) {
      if (isDragZone(e.target)) e.preventDefault();
    });

    /* ------------------------------------------------------------------
       Pauses
       ------------------------------------------------------------------ */

    panel.addEventListener('pointerenter', function (e) {
      if (e.pointerType !== 'mouse') return;
      isHovering = true;
      stop();
    });

    panel.addEventListener('pointerleave', function (e) {
      if (e.pointerType !== 'mouse') return;
      isHovering = false;
      start();
    });

    panel.addEventListener('focusin', function (e) {
      var keyboard = false;
      try { keyboard = e.target.matches(':focus-visible'); } catch (err) { keyboard = false; }
      if (!keyboard) return;
      isKeyboardFocus = true;
      stop();
    });

    panel.addEventListener('focusout', function (e) {
      if (e.relatedTarget && panel.contains(e.relatedTarget)) return;
      if (!isKeyboardFocus) return;
      isKeyboardFocus = false;
      start();
    });

    /* Flèches du clavier quand le focus est dans le panneau */
    panel.addEventListener('keydown', function (e) {
      if (n < 2) return;
      if (e.key === 'ArrowLeft') { prev(); }
      else if (e.key === 'ArrowRight') { next(); }
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop(); else start();
    });

    var onMotionChange = function () {
      if (reducedMotion.matches) stop(); else start();
    };
    if (typeof reducedMotion.addEventListener === 'function') {
      reducedMotion.addEventListener('change', onMotionChange);
    } else if (typeof reducedMotion.addListener === 'function') {
      reducedMotion.addListener(onMotionChange);
    }

    /* API pour l'éditeur de thème */
    panel.bhGoTo = function (index) {
      if (index === current) return;
      goTo(index, index > current ? 1 : -1);
    };

    /* ------------------------------------------------------------------
       État initial
       ------------------------------------------------------------------ */

    frames.forEach(resetFrame);
    setAria();

    if (n > 1) {
      if (document.readyState === 'complete') {
        preloadAround();
      } else {
        window.addEventListener('load', preloadAround, { once: true });
      }
      start();
    }
  }

  function initAllPanels(scope) {
    (scope || document).querySelectorAll('.bh-panel').forEach(initPanel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAllPanels(document); });
  } else {
    initAllPanels(document);
  }

  /* ==========================================================================
     ÉDITEUR DE THÈME SHOPIFY
     ========================================================================== */

  document.addEventListener('shopify:section:load', function (event) {
    initAllPanels(event.target);
  });

  document.addEventListener('shopify:block:select', function (event) {
    var panel = event.target && event.target.closest ? event.target.closest('.bh-panel') : null;
    if (panel && typeof panel.bhGoTo === 'function') panel.bhGoTo(0);
  });
})();
